import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { saveTeacherSession, acBase, Spinner } from './academyUtils';
import { hashPassword } from '../../utils/auth';


export default function TeacherLogin({ onSuccess, onAdminSuccess, onBack }) {
  const [mode,       setMode]       = useState('teacher'); // 'teacher' | 'admin'
  const [loginType,  setLoginType]  = useState('email');   // 'email' | 'pin'

  // Email+password form
  const [email,      setEmail]      = useState('');
  const [password,   setPassword]   = useState('');

  // Legacy PIN form (dropdown + PIN)
  const [teachers,   setTeachers]   = useState([]);
  const [loadingT,   setLoadingT]   = useState(false);
  const [loadError,  setLoadError]  = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [pin,        setPin]        = useState('');

  // Admin
  const [adminPin,   setAdminPin]   = useState('');

  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState('');

  // Setup flow (first login — set WA + new PIN)
  const [step,       setStep]       = useState('login');
  const [pendingT,   setPendingT]   = useState(null);
  const [newWA,      setNewWA]      = useState('');
  const [newPin,     setNewPin]     = useState('');
  const [confirmPin, setConfirmPin] = useState('');

  // Load teacher list when switching to PIN mode
  useEffect(() => {
    if (loginType !== 'pin' || teachers.length > 0) return;
    loadPinTeachers();
  }, [loginType]);

  async function loadPinTeachers() {
    setLoadingT(true); setLoadError('');
    try {
      const { data, error: err } = await supabase.from('teachers').select('id,name').eq('active', true).order('name');
      if (err || !data) setLoadError('Could not load teacher list. Please check your connection.');
      else {
        setTeachers(data);
        if (data.length === 0) setLoadError('No active teachers found. Contact Mr. Ahmed Salem.');
      }
    } catch {
      setLoadError('Could not load teacher list. Please check your connection.');
    }
    setLoadingT(false);
  }

  const inp = { ...acBase.inp };
  const lbl = { ...acBase.lbl };

  // ── Email + password login ────────────────────────────────────────────────
  async function handleEmailLogin() {
    setError('');
    const emailTrim = email.trim().toLowerCase();
    if (!emailTrim) { setError('Please enter your email address.'); return; }
    if (!password)  { setError('Please enter your password.'); return; }
    setLoading(true);
    try {
      const { data, error: err } = await supabase
        .from('teachers').select('*').eq('email', emailTrim).single();
      if (err || !data) { setError('No teacher account found with this email. Contact Mr. Ahmed Salem.'); setLoading(false); return; }
      if (!data.active) { setError('Your account is not active. Contact Mr. Ahmed Salem.'); setLoading(false); return; }

      if (!data.password_hash) {
        setError('No password is set for your account. Ask Mr. Ahmed Salem to set a temporary password, or use the PIN option below.');
        setLoading(false); return;
      }

      const hashed = await hashPassword(password);
      if (hashed !== data.password_hash) { setError('Incorrect password. Please try again or contact Mr. Ahmed Salem.'); setLoading(false); return; }

      saveTeacherSession(data);
      onSuccess(data);
    } catch (e) {
      setError(e.message || 'Connection error. Please try again.');
    } finally { setLoading(false); }
  }

  // ── Legacy PIN login ──────────────────────────────────────────────────────
  async function handlePinLogin() {
    setError('');
    if (!selectedId) { setError('Please select your name from the list.'); return; }
    if (pin.length < 4) { setError('Please enter your 4-digit PIN.'); return; }
    setLoading(true);
    try {
      const { data, error: err } = await supabase
        .from('teachers').select('*').eq('id', selectedId).single();
      if (err || !data) throw new Error('Teacher not found.');
      if (!data.active) { setError('Your account is not active. Contact Mr. Ahmed Salem.'); setLoading(false); return; }

      if (!data.pin || data.pin === '0000') {
        setPendingT(data);
        setStep('setup');
        setLoading(false);
        return;
      }

      if (data.pin !== pin) { setError('Incorrect PIN. Please try again or contact Mr. Ahmed Salem.'); setLoading(false); return; }

      saveTeacherSession(data);
      onSuccess(data);
    } catch (e) {
      setError(e.message || 'Connection error. Please try again.');
    } finally { setLoading(false); }
  }

  async function handleSetup() {
    setError('');
    const wa = newWA.trim().replace(/\D/g, '');
    if (wa && wa.length < 7) { setError('Please enter a valid WhatsApp number (or leave blank).'); return; }
    if (newPin.length !== 4 || !/^\d{4}$/.test(newPin)) { setError('PIN must be exactly 4 digits.'); return; }
    if (newPin !== confirmPin) { setError('PINs do not match.'); return; }
    setLoading(true);
    try {
      const upd = { pin: newPin };
      if (wa) upd.whatsapp = wa;
      const { data, error: err } = await supabase
        .from('teachers').update(upd).eq('id', pendingT.id).select().single();
      if (err) throw err;
      saveTeacherSession(data);
      onSuccess(data);
    } catch {
      setError('Could not save your PIN. Please try again.');
    } finally { setLoading(false); }
  }

  async function handleAdminLogin() {
    setError('');
    if (!adminPin) { setError('Please enter your admin password.'); return; }
    setLoading(true);
    try {
      const hash = await hashPassword(adminPin);
      const { data, error: err } = await supabase.from('admin_config').select('password_hash').eq('id', 1).single();
      if (err || !data) {
        setError('Admin login not configured yet. Contact the system administrator.');
        setLoading(false);
        return;
      }
      if (hash === data.password_hash) {
        if (onAdminSuccess) onAdminSuccess();
        else onSuccess({ id: 'admin', name: 'Admin', isAdmin: true });
      } else {
        setError('Incorrect admin password. Please try again.');
      }
    } catch {
      setError('Connection error. Please try again.');
    }
    setLoading(false);
  }

  return (
    <div style={{ minHeight:'100vh', background:'linear-gradient(135deg,#f8f9fb,#eef2f9)', display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div style={{ width:'100%', maxWidth:420 }}>

        {/* Logo */}
        <div style={{ textAlign:'center', marginBottom:28 }}>
          <img src="/logo.png" alt="IELTS Compass" style={{ height:48, objectFit:'contain', marginBottom:8 }}/>
          <div style={{ fontSize:11, color:'#b8860b', fontFamily:"'JetBrains Mono',monospace", letterSpacing:'.18em', textTransform:'uppercase' }}>
            {mode === 'teacher' ? 'Teacher Portal' : 'Admin Panel'}
          </div>
        </div>

        <div style={{ ...acBase.card, borderRadius:16, padding:28 }}>

          {/* Mode tabs */}
          {step === 'login' && (
            <div style={{ display:'flex', gap:0, marginBottom:22, borderRadius:10, overflow:'hidden', border:'1px solid #e8edf5' }}>
              {[['teacher','👩‍🏫 Teacher Login'],['admin','🔐 Admin Login']].map(([m,l])=>(
                <button key={m} onClick={()=>{ setMode(m); setError(''); setPin(''); setAdminPin(''); setPassword(''); }}
                  style={{ flex:1, padding:'10px 0', border:'none', cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:13, fontWeight:mode===m?600:400,
                    background:mode===m?'#1a2a4a':'#f8f9fb', color:mode===m?'#fff':'#5a6a88', transition:'all .15s' }}>
                  {l}
                </button>
              ))}
            </div>
          )}

          {/* ── TEACHER LOGIN — EMAIL+PASSWORD ── */}
          {mode === 'teacher' && step === 'login' && loginType === 'email' && (
            <>
              <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:22, fontWeight:700, color:'#1a2a4a', marginBottom:4 }}>Teacher Login</div>
              <div style={{ fontSize:13, color:'#5a6a88', marginBottom:20, lineHeight:1.6 }}>Enter your email and password to access your dashboard.</div>

              <label style={lbl}>Email Address</label>
              <input type="email" value={email} onChange={e=>setEmail(e.target.value)}
                onKeyDown={e=>e.key==='Enter'&&handleEmailLogin()}
                placeholder="your@email.com" style={inp} autoFocus/>

              <label style={lbl}>Password</label>
              <input type="password" value={password} onChange={e=>setPassword(e.target.value)}
                onKeyDown={e=>e.key==='Enter'&&handleEmailLogin()}
                placeholder="••••••••" style={inp}/>

              {error && <div style={acBase.err}>{error}</div>}

              <button onClick={handleEmailLogin} disabled={loading}
                style={{ width:'100%', background:'linear-gradient(135deg,#1a2a4a,#2a3a5a)', color:'#fff', border:'none', padding:'13px', borderRadius:10, fontSize:14, fontWeight:600, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif", boxShadow:'0 2px 10px rgba(26,42,74,0.25)', opacity:loading?0.7:1, display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
                {loading ? <Spinner/> : 'Access Dashboard →'}
              </button>

              <div style={{ textAlign:'center', marginTop:14 }}>
                <button onClick={()=>{ setLoginType('pin'); setError(''); }}
                  style={{ background:'none', border:'none', color:'#9aa3b8', fontSize:12, cursor:'pointer', textDecoration:'underline' }}>
                  Use PIN instead (legacy)
                </button>
              </div>
            </>
          )}

          {/* ── TEACHER LOGIN — LEGACY PIN ── */}
          {mode === 'teacher' && step === 'login' && loginType === 'pin' && (
            <>
              <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:22, fontWeight:700, color:'#1a2a4a', marginBottom:4 }}>Teacher Login</div>
              <div style={{ fontSize:13, color:'#5a6a88', marginBottom:20, lineHeight:1.6 }}>Select your name and enter your 4-digit PIN.</div>

              <label style={lbl}>Your Name</label>
              {loadingT ? (
                <div style={{ ...inp, display:'flex', alignItems:'center', gap:8, color:'#9aa3b8', marginBottom:14 }}><Spinner/> Loading teacher list…</div>
              ) : loadError ? (
                <div style={{ ...acBase.err, marginBottom:14 }}>
                  ⚠️ {loadError}
                  <br/>
                  <button onClick={loadPinTeachers}
                    style={{ marginTop:8, fontSize:12, color:'#1a5fa8', background:'none', border:'none', cursor:'pointer', padding:0, textDecoration:'underline' }}>
                    Try again
                  </button>
                </div>
              ) : (
                <select value={selectedId} onChange={e=>setSelectedId(e.target.value)} style={{ ...inp }}>
                  <option value="">— Select your name —</option>
                  {teachers.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              )}

              <label style={lbl}>4-Digit PIN</label>
              <input type="password" value={pin} onChange={e=>setPin(e.target.value.slice(0,4))}
                onKeyDown={e=>e.key==='Enter'&&handlePinLogin()}
                placeholder="••••" maxLength={4} style={inp}/>

              {error && <div style={acBase.err}>{error}</div>}

              <button onClick={handlePinLogin} disabled={loading || loadingT || !!loadError}
                style={{ width:'100%', background:'linear-gradient(135deg,#1a2a4a,#2a3a5a)', color:'#fff', border:'none', padding:'13px', borderRadius:10, fontSize:14, fontWeight:600, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif", boxShadow:'0 2px 10px rgba(26,42,74,0.25)', opacity:(loading||loadingT||!!loadError)?0.7:1, display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
                {loading ? <Spinner/> : 'Access Dashboard →'}
              </button>

              <div style={{ textAlign:'center', marginTop:14 }}>
                <button onClick={()=>{ setLoginType('email'); setError(''); }}
                  style={{ background:'none', border:'none', color:'#9aa3b8', fontSize:12, cursor:'pointer', textDecoration:'underline' }}>
                  ← Use email & password
                </button>
              </div>
            </>
          )}

          {/* ── ADMIN LOGIN ── */}
          {mode === 'admin' && step === 'login' && (
            <>
              <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:22, fontWeight:700, color:'#1a2a4a', marginBottom:4 }}>Admin Access</div>
              <div style={{ fontSize:13, color:'#5a6a88', marginBottom:20, lineHeight:1.6 }}>Enter your admin PIN to access the full admin panel.</div>

              <label style={lbl}>Admin PIN</label>
              <input type="password" value={adminPin} onChange={e=>setAdminPin(e.target.value)}
                onKeyDown={e=>e.key==='Enter'&&handleAdminLogin()}
                placeholder="••••••••" style={inp} autoFocus/>

              {error && <div style={acBase.err}>{error}</div>}

              <button onClick={handleAdminLogin} disabled={loading}
                style={{ width:'100%', background:'linear-gradient(135deg,#b8860b,#d4a017)', color:'#fff', border:'none', padding:'13px', borderRadius:10, fontSize:14, fontWeight:600, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif", boxShadow:'0 2px 10px rgba(184,134,11,0.3)', opacity:loading?0.7:1, display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
                {loading ? <Spinner/> : 'Enter Admin Panel →'}
              </button>
            </>
          )}

          {/* ── FIRST-LOGIN SETUP (PIN flow only) ── */}
          {step === 'setup' && (
            <>
              <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:22, fontWeight:700, color:'#1a2a4a', marginBottom:4 }}>Set Your PIN</div>
              <div style={{ fontSize:13, color:'#5a6a88', marginBottom:6, lineHeight:1.6 }}>
                Welcome, <strong>{pendingT?.name}</strong>! This is your first login. Please set a personal 4-digit PIN.
              </div>
              <div style={{ background:'#eef5ff', border:'1px solid #c0d8f8', borderRadius:9, padding:'10px 14px', marginBottom:18, fontSize:12.5, color:'#1a5fa8', lineHeight:1.6 }}>
                You can also add your WhatsApp number so students can see it (optional).
              </div>

              <label style={lbl}>WhatsApp Number (optional)</label>
              <input value={newWA} onChange={e=>setNewWA(e.target.value)}
                placeholder="e.g. 201012345678" style={inp}/>

              <label style={lbl}>New 4-Digit PIN</label>
              <input type="password" value={newPin} onChange={e=>setNewPin(e.target.value.replace(/\D/g,'').slice(0,4))}
                placeholder="••••" maxLength={4} style={inp}/>

              <label style={lbl}>Confirm PIN</label>
              <input type="password" value={confirmPin} onChange={e=>setConfirmPin(e.target.value.replace(/\D/g,'').slice(0,4))}
                onKeyDown={e=>e.key==='Enter'&&handleSetup()}
                placeholder="••••" maxLength={4} style={inp}/>

              {error && <div style={acBase.err}>{error}</div>}

              <button onClick={handleSetup} disabled={loading}
                style={{ width:'100%', background:'linear-gradient(135deg,#1a2a4a,#2a3a5a)', color:'#fff', border:'none', padding:'13px', borderRadius:10, fontSize:14, fontWeight:600, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif", boxShadow:'0 2px 10px rgba(26,42,74,0.25)', opacity:loading?0.7:1, display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
                {loading ? <Spinner/> : 'Save PIN & Enter Dashboard →'}
              </button>
            </>
          )}

          <div style={{ textAlign:'center', marginTop:18 }}>
            <button onClick={onBack} style={acBase.back}>← Back to main site</button>
          </div>
        </div>
      </div>
    </div>
  );
}
